import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import InvoiceEditLoadingModal from '@/enterprise/components/OfferBuilder/EditInvoices/InvoiceEditLoadingModal';
import useInvoiceEdits, { EditError } from '@/enterprise/components/OfferBuilder/EditInvoices/useInvoiceEdits';
import useDeleteRecurringRule from '@/enterprise/data/useDeleteRecurringRule';
import { TakerMarketWithRecurringRule } from '@/enterprise/data/useRecurringRules';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import InvoiceReinclusion, { InvoiceReinclusionProps } from './InvoiceReinclusion';

interface DeleteRuleModalProps {
  onClose: () => void;
  open: boolean;
  referrer: 'rules-manager' | 'division-details';
  takerMarketWithRule: TakerMarketWithRecurringRule;
}

const DeleteRuleModal = ({ open, onClose, referrer, takerMarketWithRule }: DeleteRuleModalProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const showSnackbar = useSnackbar();
  const [showError, setShowError] = useState(false);
  const [contactSRM, setContactSRM] = useState(false);
  const [handleInvoices, setHandleInvoices] = useState<'include' | 'exclude'>('include');
  const { mutateAsync: deleteRecurringRule, isPending } = useDeleteRecurringRule();
  const { asCurrency } = useLocaleFormat();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [errors, setErrors] = useState<EditError[]>([]);
  const { handleIncludeAllInvoices } = useInvoiceEdits({
    onLoadingChange: (isLoading) => {
      setLoading(isLoading);
      if (!isLoading) {
        setProgress(null);
      }
    },
    onProgressChange: (current, total) => setProgress({ current, total }),
    onError: (editErrors) => setErrors(editErrors),
    subscribeToMarketStats: true,
  });

  if (!takerMarketWithRule) {
    return null;
  }

  const { rule, takerMarket } = takerMarketWithRule;
  const excludedInvoicesCount = takerMarket.takerExcludedInvoiceCount;
  const excludedInvoicesAmount = asCurrency(takerMarket.takerExcludedInvoiceAmount, takerMarket.currency);

  const handleClose = () => {
    setContactSRM(false);
    setHandleInvoices('include');
    onClose();
  };

  const handleInvoicesChange = (value: 'include' | 'exclude') => {
    setHandleInvoices(value);
  };

  const deleteRule = async () => {
    trackEnterpriseEvent('recurring-rules::delete::invoice-reinclusion', { type: handleInvoices, ruleId: rule.id });

    await deleteRecurringRule(
      {
        id: rule.id,
        makerOrganizationUuid: rule.makerOrganizationUuid,
        marketId: rule.marketId,
        takerId: rule.takerId,
        takerUuid: rule.takerUuid,
        marketUuid: rule.marketUuid,
      },
      {
        onSuccess: () => {
          handleClose();
          showSnackbar({ message: t('automation.deleteAllRulesSuccess') });
          trackEnterpriseEvent('recurring-rules::bulk-delete::division', {
            makerOrganizationUuid: rule.makerOrganizationUuid,
            referrer,
            takerMarkets: [
              {
                marketId: rule.marketId,
                takerId: rule.takerId,
                marketUuid: rule.marketUuid,
                takerUuid: rule.takerUuid,
              },
            ],
          });
        },
        onError: () => {
          setShowError(true);
        },
      }
    );

    if (handleInvoices === 'include' && excludedInvoicesCount > 0) {
      await handleIncludeAllInvoices([takerMarket]);
    }
  };

  const handleCloseAfterError = () => {
    setErrors([]);
    setLoading(false);
    onClose();
  };

  const invoiceReinclusionProps = {
    excludedInvoicesCount,
    excludedInvoicesAmount,
    handleInvoices,
    handleInvoicesChange,
  } satisfies InvoiceReinclusionProps;

  return (
    <>
      <Modal disableOutsideClickClose={isPending} onClose={handleClose} open={open} size="sm">
        <ModalTitleInverse>{t('automation.deleteInvoiceRules')}</ModalTitleInverse>
        <ModalContent className="space-y-4">
          {showError && (
            <>
              {contactSRM ? (
                <Alert
                  description={t('core.contactSrm.description')}
                  full
                  title={t('core.contactSrm.title')}
                  type="info"
                />
              ) : (
                <Alert
                  action={{
                    onClick: () => {
                      // fire track event for slack channel
                      trackEnterpriseEvent('recurring-rules::error', { type: 'delete', ruleId: rule.id });
                      setContactSRM(true);
                    },
                    text: t('core.contactSrm'),
                  }}
                  description={t('core.reachOutForAssistance')}
                  full
                  title={t('recurringRules.error.deleteRules')}
                  type="error"
                />
              )}
            </>
          )}
          <div>{t('automation.deleteInvoiceRulesWarning')}</div>
          <ul className="ml-4 list-disc space-y-1">
            <li>{t('recurringRules.deleteParticipationDescription')}</li>
            <li>{t('recurringRules.deleteFutureInvoicesDescription')}</li>
          </ul>
          <InvoiceReinclusion {...invoiceReinclusionProps} />
        </ModalContent>
        <ModalActions>
          <Button onClick={handleClose} variant="secondary">
            {t('core.cancel')}
          </Button>
          <Button loading={isPending} onClick={deleteRule} variant="destructive">
            {t('core.delete')}
          </Button>
        </ModalActions>
      </Modal>
      <InvoiceEditLoadingModal
        errors={errors}
        onClearErrors={() => setErrors([])}
        onClose={handleCloseAfterError}
        open={loading}
        progress={progress}
      />
    </>
  );
};

export default DeleteRuleModal;
